import { useEffect } from 'react';

import { useCapability } from '../../../hooks/use-capability';
import { api } from '../../../lib/api-client';

/** Cap opt-in ÚNICA da frente (server: `conversation-viewing-awareness`). */
const CONVERSATION_VIEWING_CAPABILITY = 'conversation-viewing-awareness';

/**
 * Heartbeat < TTL do servidor (60s) — uma batida perdida não derruba o foco. O
 * close explícito (cleanup) zera na hora.
 */
const VIEWING_HEARTBEAT_MS = 25_000;

/**
 * useConversationViewing — Frente engine-led S7 (Condução Governada, Peça 1).
 *
 * Sinaliza ao Engine que este operador está OLHANDO a conversa: `viewing:true`
 * no montar (+ heartbeat), `viewing:false` no desmontar. Alimenta o alerta de
 * cliente-em-espera (não avisa quem já está com a conversa aberta).
 *
 * **Gateia o EMIT, não só o render** (memória `feedback_gatear_fetch_nao_so_render`):
 * quando a cap está OFF o hook não dispara nenhuma chamada (o endpoint estaria
 * 403 e a feature é inerte em produção). Fire-and-forget — falhas de rede não
 * afetam a UI (o TTL do servidor cobre batidas perdidas).
 */
export function useConversationViewing(conversationId: string | undefined): void {
  const enabled = useCapability(CONVERSATION_VIEWING_CAPABILITY);

  useEffect(() => {
    if (!enabled || conversationId === undefined) return;

    let active = true;
    const emit = (viewing: boolean): void => {
      void api.setConversationViewing(conversationId, viewing).catch(() => {
        // best-effort: o TTL do servidor tolera batidas perdidas.
      });
    };

    emit(true);
    const interval = setInterval(() => {
      if (active) emit(true);
    }, VIEWING_HEARTBEAT_MS);

    return () => {
      active = false;
      clearInterval(interval);
      emit(false);
    };
  }, [enabled, conversationId]);
}
